import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { AppointmentTypes } from '../../../types/appointment.types';
import { IN_PROGRESS_COLUMNS } from '../../../builders/appointments-builders';
import { AppointmentStatusCell } from './appointment-status-cell';
import { LocationCell } from './location-cell';

/**
 * In-Progress section for the Active & Future tab.
 *
 * Shows appointments where isInProgress === true with:
 *   - Pulsing success dot in section header
 *   - Elapsed time (red text when expired/escalated)
 *   - Live status lifecycle
 */

interface Props {
    items: AppointmentTypes.Model[];
    onRowClick: (appointment: AppointmentTypes.Model) => void;
}

export const InProgressSection: React.FC<Props> = ({ items, onRowClick }) => {
    const theme = useTheme();

    if (!items || items.length === 0) return null;

    const formatElapsed = (minutes: number | null | undefined): string => {
        if (minutes == null) return '--';
        const hrs = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        if (hrs > 0) return `${hrs}h ${mins}m`;
        return `${mins}m`;
    };

    return (
        <Box>
            {/* Section header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.success.main,
                        animation: 'inProgressPulse 2s ease-in-out infinite',
                        '@keyframes inProgressPulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                        },
                    }}
                />
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                    In Progress
                </Typography>
                <Box
                    sx={{
                        backgroundColor: theme.palette.success.light,
                        borderRadius: 1,
                        px: 1,
                        py: 0.25,
                    }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {items.length}
                    </Typography>
                </Box>
                <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', ml: 'auto' }}
                >
                    As of {dayjs().format('h:mm A')}
                </Typography>
            </Box>

            {/* Table */}
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {IN_PROGRESS_COLUMNS.map((col) => (
                                <TableCell key={col.field}>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                        {col.headerName}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((appt) => {
                            const isAlertState = appt.liveStatus === 'expired' || appt.liveStatus === 'escalated';

                            return (
                                <TableRow
                                    key={appt.id}
                                    sx={{
                                        ':hover': {
                                            backgroundColor: theme.palette.action.hover,
                                            cursor: 'pointer',
                                        },
                                    }}
                                    onClick={() => onRowClick(appt)}
                                >
                                    {/* Scheduled Time */}
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {appt.startDateTime
                                                ? dayjs(appt.startDateTime).format('MM/DD/YYYY h:mm A')
                                                : '--'}
                                        </Typography>
                                    </TableCell>

                                    {/* Title */}
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {appt.title || '--'}
                                        </Typography>
                                    </TableCell>

                                    {/* User Name */}
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {appt.user?.firstName && appt.user?.lastName
                                                ? `${appt.user.firstName} ${appt.user.lastName}`
                                                : '[Deleted User]'}
                                        </Typography>
                                    </TableCell>

                                    {/* Appt Location */}
                                    <TableCell>
                                        <LocationCell location={appt.location} />
                                    </TableCell>

                                    {/* Checked-In Location */}
                                    <TableCell>
                                        <LocationCell
                                            location={appt.checkInLocation}
                                            isGps={!!appt.checkIn}
                                        />
                                    </TableCell>

                                    {/* Check-In */}
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {appt.checkIn
                                                ? dayjs(appt.checkIn).format('h:mm A')
                                                : '--'}
                                        </Typography>
                                    </TableCell>

                                    {/* Elapsed */}
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: isAlertState
                                                    ? theme.palette.error.main
                                                    : 'text.secondary',
                                                fontWeight: isAlertState ? 600 : 400,
                                            }}
                                        >
                                            {formatElapsed(appt.elapsed)}
                                        </Typography>
                                    </TableCell>

                                    {/* Status */}
                                    <TableCell>
                                        <AppointmentStatusCell
                                            status={appt.liveStatus || 'not-checked-in'}
                                            variant="live"
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};
